import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Truck, ShieldCheck, Sprout, ArrowRight, Phone, MapPin, MessageCircle, Facebook } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/store/ProductCard";
import { FloatingContacts } from "@/components/store/StoreFooter";
import { useSettings } from "@/context/SettingsContext";

const HERO = "https://images.unsplash.com/photo-1695229014950-ab9338dd2c44?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category");
  const search = params.get("search");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { settings } = useSettings();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (search) p.search = search;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, search]);

  const featured = products.filter((p) => p.featured);
  const filtered = category || search;

  return (
    <div className="pb-10">
      <FloatingContacts settings={settings} />

      {!filtered && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={HERO} alt="chatal" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#14281D]/95 via-[#1E3A2B]/85 to-[#1E3A2B]/40" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
              <span className="inline-block bg-[#CA8A04] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">{settings.tagline || "মানসম্মত চাল ও ধানজাত পণ্যের বিশ্বস্ত ঠিকানা"}</span>
              <h1 className="text-3xl sm:text-5xl font-bold text-white leading-tight tracking-tight">
                মানসম্মত চাল ও ধানজাত পণ্য, <span className="text-[#FEF08A]">সঠিক দামে</span>
              </h1>
              <p className="mt-4 text-stone-200 text-base sm:text-lg">চাল, ব্রান, খুদ, তুষ, গুঁড়া ও অন্যান্য ধানজাত পণ্য অর্ডার করুন সহজেই — {settings.business_name || "M.D.Rice Mill"} থেকে দ্রুত হোম ডেলিভারি।</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#products" className="bg-[#CA8A04] hover:bg-[#A16207] text-white font-medium px-6 h-12 rounded-xl flex items-center gap-2 transition-colors">
                  এখনই কিনুন <ArrowRight size={18} />
                </a>
                <Link to="/track" className="bg-white/10 backdrop-blur border border-white/30 text-white font-medium px-6 h-12 rounded-xl flex items-center transition-colors hover:bg-white/20">
                  অর্ডার ট্র্যাক করুন
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {!filtered && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[[Truck, "দ্রুত ডেলিভারি", "সারা দেশে হোম ডেলিভারি"], [ShieldCheck, "গুণগত নিশ্চয়তা", "পরিষ্কার ও খাঁটি চাল"], [Sprout, "সরাসরি মিল থেকে", "মধ্যস্বত্বভোগী ছাড়াই সৎ দাম"]].map(([Icon, t, d], i) => (
              <div key={i} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><Icon size={22} /></div>
                <div><div className="font-semibold text-[#1C1917]">{t}</div><div className="text-xs text-stone-500">{d}</div></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!filtered && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <h2 className="text-2xl font-bold text-[#1E3A2B] mb-4">ক্যাটাগরি</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {categories.map((c) => (
              <Link key={c.id} to={`/?category=${c.slug}`} data-testid={`category-${c.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[3/2] border border-stone-200 shadow-sm">
                <img src={c.image} alt={c.name_bn} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-3">
                  <span className="text-white font-semibold">{c.name_bn}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section id="products" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-[#1E3A2B]">
            {search ? `"${search}" এর ফলাফল` : category ? categories.find((c) => c.slug === category)?.name_bn || "পণ্যসমূহ" : "সকল পণ্য"}
          </h2>
          {filtered && <Link to="/" className="text-sm text-emerald-700 font-medium">সব দেখুন</Link>}
        </div>

        {!filtered && featured.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#CA8A04] mb-3">🌟 ফিচার্ড পণ্য</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {featured.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}

        <h3 className="text-lg font-semibold text-[#1E3A2B] mb-3">{filtered ? "" : "সব পণ্য"}</h3>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="bg-white rounded-2xl border border-stone-200 aspect-[3/4] animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 text-stone-500">
            <Search size={40} className="mx-auto mb-3 opacity-40" />
            কোনো পণ্য পাওয়া যায়নি
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {!filtered && (
        <section id="about" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="about-section">
              <h2 className="text-2xl font-bold text-[#1E3A2B] mb-3">আমাদের সম্পর্কে</h2>
              <p className="text-stone-600 leading-relaxed">{settings.business_description || "খাঁটি ও উন্নত মানের চাল সরাসরি চাতাল থেকে আপনার ঘরে।"}</p>
              {settings.delivery_info && (
                <div className="mt-4 flex items-start gap-2 text-sm text-stone-600">
                  <Truck size={18} className="text-emerald-700 mt-0.5 shrink-0" />
                  <span data-testid="home-delivery-info">{settings.delivery_info}</span>
                </div>
              )}
            </div>
            <div className="bg-[#14281D] text-stone-200 rounded-2xl p-6" data-testid="contact-section">
              <h2 className="text-2xl font-bold text-white mb-3">যোগাযোগ</h2>
              <ul className="space-y-3 text-sm">
                {[settings.phone, settings.phone2, settings.phone3].filter(Boolean).map((ph, i) => (
                  <li key={i} className="flex items-center gap-2"><Phone size={16} className="text-[#CA8A04]" /> <a href={`tel:${ph}`} data-testid={`home-phone-${i}`} className="hover:text-white">{ph}</a></li>
                ))}
                <li className="flex items-start gap-2" data-testid="home-address"><MapPin size={16} className="text-[#CA8A04] mt-0.5" /> {settings.address || "ধড়মোকাম, শেরপুর, বগুড়া"}</li>
                {settings.whatsapp && (
                  <li>
                    <a href={`https://wa.me/${(settings.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="home-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 h-10 rounded-xl font-medium">
                      <MessageCircle size={16} /> হোয়াটসঅ্যাপে বার্তা দিন
                    </a>
                  </li>
                )}
                {settings.facebook && (
                  <li>
                    <a href={settings.facebook} target="_blank" rel="noreferrer" data-testid="home-facebook" className="inline-flex items-center gap-2 text-[#CA8A04] hover:underline">
                      <Facebook size={16} /> আমাদের ফেসবুক পেজ
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
